import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenStore } from '../stores/useScreenStore';
import { useAuthStore } from '../stores/useAuthStore';
import { logout } from '../services/authService';
import { useCurrentHash } from '../hooks/hashs/useCurrentHash';
import { useThemeModeStore } from '../stores/useThemeModeStore';
import { useClientsStore } from '../stores/useClientsStore';
import { useLinesStore, getLinhasFiltradas } from '../stores/useLinesStore';
import { useOrderStore } from '../stores/useOrderStore';
import { useListModeStore } from '../stores/useListModeStore';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';
import { IconButton, Tooltip, Container, Box, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LogoutIcon from '@mui/icons-material/Logout';
import CustomSelect from './CustomSelect';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MonitorIcon from '@mui/icons-material/Monitor';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import { useTheme } from '@mui/material/styles';
import TripFilters from './TripFilters';
import Clock from './Clock';
import DateSelector from './DateSelector';
import ExportButton from './ExportButton';
import { usePermissions } from '../hooks/auth/usePermissions';

const dontShowLocations = ['/selecionar-perfil', '/selecionar-monitores'];

const Header = () => {

  const user = useAuthStore(state => state.user);
  const perfisDisponiveis = useAuthStore(state => state.perfisDisponiveis);
  const perfisSelecionados = useAuthStore(state => state.perfisSelecionados);
  const setPerfisSelecionados = useAuthStore(state => state.setPerfisSelecionados);
  const allowPerfis = useAuthStore(state => state.allowPerfis);

  const sentidoOptions = useLinesStore(state => state.sentidoOptions);
  const sentidosSelecionados = useLinesStore(state => state.sentidosSelecionados);
  const setSentidosSelecionados = useLinesStore(state => state.setSentidosSelecionados);
  const sentidosSet = useLinesStore(state => state.sentidosSet);

  const linhasDisponiveis = useLinesStore(state => state.linhasDisponiveis);
  const linhasSelecionadas = useLinesStore(state => state.linhasSelecionadas);
  const setLinhasSelecionadas = useLinesStore(state => state.setLinhasSelecionadas);

  const allowClientes = useClientsStore(state => state.allowClientes);

  const isMainLoading = useLoadingStore(selectIsMainLoading);

  const linhasFiltradas = useMemo(() => {

    return getLinhasFiltradas({
      linhasDisponiveis,
      allowClientes,
      allowPerfis,
      sentidosSet
  });

  }, [
    linhasDisponiveis,
    allowClientes,
    allowPerfis,
    sentidosSet
  ]);

  const linhasOptions = useMemo(() => {

    return linhasFiltradas
      .map(linha => ({
        id: String(linha.CODIGO_INTEGRACAO),
        label: String(linha.CODIGO_INTEGRACAO)
      }))
      .sort((a, b) =>
        a.label.localeCompare(
            b.label,
            'pt-BR'
        )
      );

  }, [linhasFiltradas]);

  const allLinhasIds = useMemo(
    () => linhasOptions.map(l => l.id),
    [linhasOptions]
  );

  const selectedLinhas = useMemo(
    () =>
      linhasSelecionadas === null
        ? allLinhasIds
        : linhasSelecionadas,
    [linhasSelecionadas, allLinhasIds]
  );

  const perfisOptions = useMemo(
    () =>
      perfisDisponiveis.map(p => ({
        id: p.id,
        label: p.nome
      })),
    [perfisDisponiveis]
  );

  const screen = useScreenStore(state => state.screen);

  const monitorMode = useScreenStore(state => state.monitorMode);

  const fullscreenOn = useScreenStore(state => state.fullscreenOn);

  const setMonitorMode = useScreenStore(state => state.setMonitorMode);

  const setFullscreenOn = useScreenStore(state => state.setFullscreenOn);

  const screenBlocked = useScreenStore(state => state.screenBlocked); 

  const order = useOrderStore(state => state.order);
  const setOrder = useOrderStore(state => state.setOrder);

  const orderASC = useOrderStore(state => state.orderASC);
  const toggleOrderASC = useOrderStore(state => state.toggleOrderASC);

  const orderTypes = useOrderStore(state => state.orderTypes);
  
  const {
      listMode,
      toggleListMode
  } = useListModeStore();

  const {
    hashTitle,
    hashColor,
    HashIconComponent
  } = useCurrentHash();

  const location = useLocation();
  const navigate = useNavigate();
  const mode = useThemeModeStore(state => state.mode); 
  const toggleTheme = useThemeModeStore(state => state.toggleTheme); 
  const theme = useTheme();
  const headerStyles = theme.components.Header?.styleOverrides || {};

  const clientesDisponiveis = useClientsStore(state => state.clientesDisponiveis);
  const clientesSelecionados = useClientsStore(state => state.clientesSelecionados);
  const setClientesSelecionados = useClientsStore(state => state.setClientesSelecionados);


  const isCompactMode = useLayoutStore(
    state => state.isCompactMode
  );

  const toggleCompactMode = useLayoutStore(
    state => state.toggleCompactMode
  );
  

  const clientesFiltrados = useMemo(() => {
    if (!perfisSelecionados?.length) return clientesDisponiveis;

    return clientesDisponiveis.filter(cliente => {
      if (!cliente.operating_units?.length) return false;

      return cliente.operating_units.some(op =>
        perfisSelecionados.includes(Number(op))
      );
    });
  }, [clientesDisponiveis, perfisSelecionados]);

  const allClientesIds = useMemo(
    () => clientesFiltrados.map(c => c.id),
    [clientesFiltrados]
  );

  const selectedClientes = useMemo(
    () => (clientesSelecionados === null ? allClientesIds : clientesSelecionados),
    [clientesSelecionados, allClientesIds]
  );

  const {
    canSelectDualMonitor,
    canSelectProfiles
  } = usePermissions();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {

    if (!clientesFiltrados?.length) {
      return;
    }

    if (clientesSelecionados === null) {
      return;
    }

    const validos = clientesSelecionados.filter(id =>
      clientesFiltrados.some(c => c.id === id)
    );

    if (
      validos.length !==
      clientesSelecionados.length
    ) {

      setClientesSelecionados(validos);

    }

  }, [
    clientesFiltrados,
    clientesSelecionados,
    setClientesSelecionados
  ]);

  useEffect(() => {
    
    if (!allowPerfis.size) {
      return;
    }

    if (!linhasDisponiveis.length) {
      return;
    }

    if (linhasSelecionadas === null) {
      return;
    }

    const optionsIds = new Set(
      linhasOptions.map(linha => linha.id)
    );

    const validos = linhasSelecionadas.filter(id =>
      optionsIds.has(id)
    );

    if (validos.length !== linhasSelecionadas.length) {
      setLinhasSelecionadas(validos);
    }

  }, [
    allowPerfis,
    linhasDisponiveis,
    linhasOptions,
    linhasSelecionadas,
    setLinhasSelecionadas
  ]);

  if(!user || dontShowLocations.includes(location.pathname) || screenBlocked){
    return null;
  }

  return (
    <header
      style={{
          ...headerStyles.root,

          backgroundColor:
              (monitorMode === 1 || screen === 'Filtros')
                  ? theme.palette.primary.main
                  : theme.palette[hashColor]?.main,

          color:
              (monitorMode === 1 || screen === 'Filtros')
                  ? theme.palette.primary.contrastText
                  : theme.palette.getContrastText(
                      theme.palette[hashColor]?.main
                  ),

          marginBottom:
              ((monitorMode === 1 && !isCompactMode) ||
              (monitorMode === 2 && screen === 'Filtros'))
                  ? 20
                  : 0,
          transition: 'all ease-in-out 250ms'
      }}
    >
      
      {!screenBlocked &&
        <>
          {(monitorMode === 2 && screen === 'Home') &&
            <>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                gap: 1,
                width: '100%',
                alignItems: {
                  xs: 'center'
                },
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start'
                },
                paddingTop: {
                  xs: 1,
                  md: 0,
                }
              }}>
                <HashIconComponent sx={{ fontSize: {xs: 24, md: 30}}} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: {
                      xs: '1rem',
                      sm: '1.25rem',
                      md: '1.5rem'
                    },
                    fontWeight: {
                      xs: 400,
                      md: 500
                    }
                  }}
                >
                  {hashTitle}:
                </Typography>
                <ExportButton />
              </Box>           
            </>
          }

          {monitorMode === 2 && screen === 'Home' &&
            <TripFilters inheritColor={true}/>   
          }
          
          {(monitorMode === 1 || (monitorMode === 2 && screen === 'Filtros')) &&
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: isCompactMode ? 0 : 1,
                alignItems: 'center',
                [theme.breakpoints.down(900)]: {
                  flexBasis: '50%'
                },
                [theme.breakpoints.down(800)]: {
                  flexBasis: '100%'
                }
              }}
            >
              {!isCompactMode && (
                <>
                  <Typography variant='body1' style={headerStyles.userName}>Bem-vindo, {user?.name}</Typography>
                  <Clock/>
                </>
              )}              
              <DateSelector/>
            </Box>
          }

          {(monitorMode === 1 || (monitorMode === 2 && screen === 'Filtros')) &&
            <>
              {!user?.capabilities?.canSelectClients && clientesFiltrados.length === 1 ? (
                <Box
                  sx={(theme) => ({
                    border: '2px solid',
                    borderColor: theme.palette.main,
                    borderRadius: 1,

                    px: isCompactMode ? 1 : 2,
                    pt: isCompactMode ? 0.5 : 2,
                    pb: isCompactMode ? 0.5 : 2,

                    m: 2,

                    display: 'flex',
                    flexDirection: 'column',
                    gap: isCompactMode ? 0 : 1,

                    backgroundColor: 'inherit',
                    position: 'relative',

                    ...(isCompactMode && {
                        minWidth: 100
                    })
                  })}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: isCompactMode ? -7 : -10,
                      left: isCompactMode ? 8 : 12,
                      px: isCompactMode ? 0.5 : 1,
                      py: isCompactMode ? 0 : 0.2,
                      fontSize: isCompactMode ? 10 : 12,
                      backgroundColor: 'inherit',
                      color: 'inherit',
                      fontWeight: 500,
                      pointerEvents: 'none',
                      borderRadius: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Cliente
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isCompactMode ? 14 : undefined,
                      lineHeight: isCompactMode ? 1.5 : undefined,
                      pointerEvents: 'none',
                      mt: .2
                    }}
                  >
                    {clientesFiltrados[0]?.label}
                  </Typography>
                </Box>
              ) : (
                <CustomSelect
                  items={clientesFiltrados}
                  selected={selectedClientes}
                  onChange={setClientesSelecionados}
                  label="Cliente"
                  maxVisibleLabels={1}
                  multiple
                  inheritColor
                  enableSearch
                  enabledToggle
                  allowNone
                  allowAll
                  scrollToSelected
                  {...(isCompactMode && {
                      layout: 'compact'
                  })}
                />               
              )}
              <CustomSelect
                items={linhasOptions}
                selected={selectedLinhas}
                onChange={(val) => {

                  const allSelected = val.length === linhasOptions.length;

                  setLinhasSelecionadas(allSelected ? null : val);

                }}
                label="Linhas"
                maxVisibleLabels={1}
                multiple={true}
                inheritColor={true}
                enableSearch={true}
                enabledToggle={true}
                allowNone={true}
                allowAll={true}
                scrollToSelected={true}
                {...(isCompactMode && {
                  layout: 'compact'
                })}
              />

              <CustomSelect
                items={sentidoOptions}
                selected={sentidosSelecionados}
                onChange={setSentidosSelecionados}
                label="Sentido da linha"
                allowNone={false}
                allowAll={true}
                multiple
                inheritColor={true}
                {...(isCompactMode && {
                  layout: 'compact',
                  maxVisibleLabels: 1
                })}
              />    

              {canSelectProfiles &&
                <CustomSelect
                  items={perfisOptions}
                  selected={perfisSelecionados}
                  onChange={(val) => setPerfisSelecionados(val)}
                  label="Perfis de monitoramento"
                  multiple={true}
                  enableSearch={true}
                  allowNone={false}
                  allowAll={true}
                  inheritColor={true}
                  {...(isCompactMode && {
                    layout: 'compact',
                    maxVisibleLabels: 1
                  })}
                />
              }

              <CustomSelect
                items={orderTypes}
                selected={order}
                onChange={setOrder}
                orderAsc={orderASC}
                onToggleOrderAsc={toggleOrderASC}
                label="Ordenar por"
                inheritColor={true}
                {...(isCompactMode && {
                  layout: 'compact',
                  maxVisibleLabels: 1
                })}
              />       
              
            </>
          }

          {isCompactMode && (
            <Box
              sx={{
                width: '100%',
                display: 'none',

                [theme.breakpoints.down(1500)]: {
                  display: 'block'
                }
              }}
            />
          )}
            
          {screen === 'Home' &&
            <Container
              sx={{ 
                display: 'flex', 
                marginX: 0,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                flexBasis: 'fit-content',
                flexWrap: 'wrap',
                gap: 2,
                [theme.breakpoints.down(800)]: {
                  flexBasis: '100%',
                },
                color: theme.palette.getContrastText(theme.palette[hashColor]?.main)
              }}
            >
              <Tooltip
                title={
                  isCompactMode
                    ? 'Layout Completo'
                    : 'Layout Compacto'
                }
              >
                <span>
                  <IconButton
                    onClick={toggleCompactMode}
                    sx={{
                      marginY: 2,
                      color: 'inherit'
                    }}
                    disabled={monitorMode === 2}
                  >
                    {isCompactMode
                      ? (
                        <ViewComfyIcon
                          sx={{ fontSize: 28 }}
                        />
                      )
                      : (
                        <ViewCompactIcon
                          sx={{ fontSize: 28 }}
                        />
                      )
                    }
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={listMode === 'list' ? 'Modo Cards' : 'Modo Lista'}>
                <IconButton onClick={toggleListMode}
                  sx={{ 
                    marginY: 2,
                    [theme.breakpoints.down(900)]: {
                      display: 'none'
                    },
                    color: 'inherit'
                  }}
                >
                  {listMode === 'list'
                    ? <GridViewIcon />
                    : <TableRowsIcon />
                  }
                </IconButton>
              </Tooltip>
              {canSelectDualMonitor &&
                <Tooltip title={monitorMode === 1 ? '2 Telas' : '1 Tela'}
                  sx={{
                    [theme.breakpoints.down(900)]: {
                      display: 'none'
                    }
                  }}
                >
                  <span>
                    <IconButton onClick={() => setMonitorMode(monitorMode === 1 ? 2 : 1)} 
                      sx={{ 
                        marginY: 2,
                        [theme.breakpoints.down(900)]: {
                          display: 'none'
                        },
                        color: 'inherit'
                      }}
                      disabled={isMainLoading}
                    >
                      <MonitorIcon sx={{ fontSize: monitorMode === 1 ? 28 : 14}}/>
                      {monitorMode === 2 &&
                        <MonitorIcon sx={{ fontSize: 14 }} />
                      }
                    </IconButton>
                  </span>
                </Tooltip>
              }

              <Tooltip title={`Tema ${mode === 'light' ? 'Escuro' : 'Claro'}`}>
                <IconButton onClick={toggleTheme} sx={{color: 'inherit'}}>
                  {mode === 'light'
                    ? <Brightness4Icon sx={{ fontSize: 28 }} />
                    : <Brightness7Icon sx={{ fontSize: 28 }} />
                  }
                </IconButton>
              </Tooltip>

              <Tooltip title={fullscreenOn ? 'Sair da Tela Cheia' : 'Tela Cheia'}>
                <IconButton onClick={() => setFullscreenOn(!fullscreenOn)} 
                  sx={{ 
                    marginY: 2,
                    [theme.breakpoints.down(900)]: {
                      display: 'none'
                    },
                    color: 'inherit'
                  }}
                >
                  <FullscreenIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Sair">
                <span>
                  <IconButton 
                    onClick={handleLogout} 
                    sx={{ 
                      marginY: 2, color: 'inherit' 
                    }}
                    disabled={isMainLoading}
                  >
                    <LogoutIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Container>
          }
        </>
      }
    </header>
  );
};

export default Header;